import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { env } from '@/shared/config/env';
import { LocalInstallPreferences } from '@/infrastructure/storage/LocalInstallPreferences';
import { registerServiceWorker } from '@/infrastructure/pwa/ServiceWorkerAppUpdater';
import { applyAppBranding } from '@/infrastructure/pwa/appBranding';
import { keepDocumentStill } from '@/ui/layout/keepDocumentStill';
import '@/ui/tokens.css';
import '@/ui/motion.css';
import '@/ui/base.css';
import { App } from './App';
import { AppProviders } from './providers';

/**
 * Section 8.4. In the mock build MSW intercepts at the network layer, so the
 * HTTP repositories are still exercised and the serialisation is still tested.
 * The worker must be running before the first query fires, hence the await.
 */
/**
 * Section 9. The service worker gives the app shell and the reference lists a
 * cache, so a technician in the furnace corner still gets a screen rather than
 * a browser error page.
 *
 * Skipped in the mock build: MSW installs its own worker for the same scope and
 * two of them fighting over `fetch` is a debugging afternoon nobody needs.
 *
 * A new build waits rather than reloading under somebody's hands. Reloading
 * mid-task would discard an unsaved note; the outbox protects the commands, not
 * the form the technician is still filling in. It used to wait with nothing
 * telling anybody, so a phone kept its old build until every window closed; the
 * Update banner is now what ends the wait (src/features/shell/UpdateBanner.tsx).
 */
/**
 * Removes a mock worker left behind by an earlier build.
 *
 * MSW installs a service worker of its own, and a service worker OUTLIVES the
 * build that installed it. A device that once loaded a mock build kept that
 * worker registered and went on answering every API call from an in-browser
 * store, on a device that was now pointed at the real API. Two phones in the
 * same lab showed two different sets of cases and neither was wrong from where
 * it stood. Nothing short of clearing site data fixed it, and nobody knew to.
 *
 * So a real build evicts it. This is a migration for devices already in that
 * state, not a permanent need: once a handset has run this code the worker is
 * gone for good. It costs one enumeration of the registrations at startup.
 */
async function evictMockWorker(): Promise<void> {
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const mocks = registrations.filter((registration) => {
      const worker = registration.active ?? registration.waiting ?? registration.installing;
      return worker !== null && worker !== undefined && worker.scriptURL.includes('mockServiceWorker');
    });

    await Promise.all(mocks.map((registration) => registration.unregister()));
  } catch {
    // A browser that will not enumerate its registrations is not a reason to
    // hold up the application. The worst case is the state we were already in.
  }
}

async function startServiceWorker(): Promise<void> {
  if (env.useMocks || !('serviceWorker' in navigator)) return;

  // Before ours registers, so the two are never both claiming the same scope.
  await evictMockWorker();

  await registerServiceWorker();
}

async function start(): Promise<void> {
  if (env.useMocks) {
    const { startMockServer } = await import('@/infrastructure/mocks/browser');
    await startMockServer();
  }

  // Before the first render, so the install prompt sees an accurate count.
  new LocalInstallPreferences().recordVisit();
  void startServiceWorker();
  // The colours and icon management saved in Settings. Request of 5 October 2026.
  void applyAppBranding();
  // An iPhone leaves the page shifted up after its keyboard. Request of 6 October 2026.
  keepDocumentStill();

  const container = document.getElementById('root');
  if (!container) throw new Error('Root element is missing from index.html');

  createRoot(container).render(
    <StrictMode>
      <BrowserRouter>
        <AppProviders>
          <App />
        </AppProviders>
      </BrowserRouter>
    </StrictMode>,
  );
}

void start();
